import React from 'react';
import { Link } from 'react-router-dom';
import { getNovelImageUrl } from '../utils/imageUtils';
import { getBestRankingDisplay, RANK_META } from '../utils/rankingUtils';
import Card from './common/ui/Card';
import Badge from './common/ui/Badge';
import './NovelCard.css';

const joinClasses = (...classes) => classes.filter(Boolean).join(' ');

const MAX_VISIBLE_GENRES = 5; // 4 visible + 1 overflow chip = 5 items, fits 2 rows

const HighlightedText = ({ text, highlight }) => {
  if (!text) return null;
  if (!highlight || !highlight.trim()) return <>{text}</>;

  const parts = text.split(new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <mark key={i} className="highlight">
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </>
  );
};

// Tooltip text for the 18+ badge — lists the declared warnings when present
// so hovering a card gives the reader the same heads-up as the detail page.
const getMatureBadgeTitle = (novel) => {
  const names = Array.isArray(novel?.contentWarnings)
    ? novel.contentWarnings.map((w) => w?.name).filter(Boolean)
    : [];
  return names.length > 0
    ? `Mature content: ${names.join(', ')}`
    : 'Mature content (18+)';
};

const buildMetaItems = (metaItems) => {
  if (!Array.isArray(metaItems) || metaItems.length === 0) {
    return null;
  }

  return (
    <div className="novel-card__stats">
      {metaItems.map((item, index) => (
        <span key={index}>{item}</span>
      ))}
    </div>
  );
};

const renderGenres = (genres) => {
  if (!Array.isArray(genres) || genres.length === 0) {
    return null;
  }

  const normalized = genres.filter(Boolean);
  if (normalized.length === 0) {
    return null;
  }

  const hasOverflow = normalized.length >= MAX_VISIBLE_GENRES;
  const visibleLimit = hasOverflow ? MAX_VISIBLE_GENRES - 1 : normalized.length;
  const visibleGenres = normalized.slice(0, visibleLimit);
  const hiddenCount = normalized.length - visibleLimit;

  return (
    <div className="novel-card__genres" role="list">
      {visibleGenres.map((genre, index) => {
        const rawLabel = genre?.name ?? genre;
        const label = typeof rawLabel === 'string' ? rawLabel : String(rawLabel ?? '');
        const key = genre?.id ?? `${label}-${index}`;

        return (
          <Badge key={key} variant="default" size="sm" className="genre-chip" title={label}>
            {label}
          </Badge>
        );
      })}
      {hasOverflow ? (
        <Badge key="genre-overflow" variant="default" size="sm" className="genre-chip genre-chip--more">
          +{hiddenCount}
        </Badge>
      ) : null}
    </div>
  );
};

const CoverImage = ({ coverImageUrl, title, fallback, compact, loading = 'lazy', children }) => {
  // Track the failed URL rather than a boolean so a changed coverImageUrl
  // retries naturally — a failed cover degrades to the title placeholder
  // instead of the browser's broken-image icon.
  const [failedSrc, setFailedSrc] = React.useState(null);
  return (
    <div className={joinClasses('novel-card__cover', compact && 'novel-card__cover--compact')}>
      {coverImageUrl && failedSrc !== coverImageUrl ? (
        <img
          src={coverImageUrl}
          alt={title}
          width="200"
          height="300"
          loading={loading}
          onError={() => setFailedSrc(coverImageUrl)}
        />
      ) : (
        <div className="novel-card__placeholder" aria-hidden="true">
          <span>{fallback}</span>
        </div>
      )}
      {children}
    </div>
  );
};

const NovelCard = ({
  novel,
  to,
  onClick,
  variant = 'standard',
  rank,
  rankType,
  status,
  genres,
  description,
  actions,
  className,
  highlight,
  customFooter,
  metaItems,
  coverBadge,
  loading = 'lazy',
}) => {
  const isLink = !!to;
  const isInteractive = isLink || !!onClick;

  // Auto-derive rank display from novel.rankings if no explicit rank prop was provided
  const rankDisplay = rank !== undefined
    ? { rankPosition: rank, ...(RANK_META[rankType] || { label: null, cssModifier: null }) }
    : getBestRankingDisplay(novel?.rankings);

  // Helper to format views
  const formatViews = (count) => {
    if (!count) return '0';
    if (count >= 1000000) {
      const val = (count / 1000000).toFixed(1);
      return (val.endsWith('.0') ? val.slice(0, -2) : val) + 'M';
    }
    if (count >= 1000) {
      const val = (count / 1000).toFixed(1);
      return (val.endsWith('.0') ? val.slice(0, -2) : val) + 'K';
    }
    return count.toString();
  };

  const cardContent = (
    <>
      {variant === 'compact' ? (
        <>
          <CoverImage
            coverImageUrl={getNovelImageUrl(novel)}
            title={novel.title}
            fallback="📚"
            compact
            loading={loading}
          >
            {rankDisplay && (
              <div className={joinClasses('novel-card__rank-badge', 'novel-card__rank-badge--compact', rankDisplay.cssModifier && `novel-card__rank-badge--${rankDisplay.cssModifier}`)}>
                <span className="rank-number">#{rankDisplay.rankPosition}</span>
              </div>
            )}
            {status && (
              <div className={joinClasses('novel-card__status-badge', 'novel-card__status-badge--compact')}>
                <span className={`status-dot ${status.toLowerCase()}`}></span>
                {status}
              </div>
            )}
            {novel.isFanFiction && (
              <div className={joinClasses('novel-card__fanfic-badge', 'novel-card__fanfic-badge--compact')}>Fan Fiction</div>
            )}
            {novel.contentRating === 'MATURE' && (
              <div
                className={joinClasses('novel-card__mature-badge', 'novel-card__mature-badge--compact')}
                title={getMatureBadgeTitle(novel)}
              >18+</div>
            )}
          </CoverImage>
          <div className="novel-card__body">
            <div className="novel-card__compact-head">
              <h3 className="novel-card__title">
                <HighlightedText text={novel.title} highlight={highlight} />
              </h3>
              {novel.author?.name && (
                <p className="novel-card__author-inline">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>
                  <HighlightedText text={novel.author.name} highlight={highlight} />
                </p>
              )}
            </div>
            {renderGenres(genres || novel.genres)}
            {description ? (
              <p className="novel-card__description">{description}</p>
            ) : null}
            <div className="novel-card__stats-compact">
              <span className="novel-card__stat-compact novel-card__stat-compact--rating">
                ⭐ {novel.averageRating ? novel.averageRating.toFixed(1) : 'N/A'}
              </span>
              <span className="novel-card__stat-sep" aria-hidden="true">•</span>
              <span className="novel-card__stat-compact">{novel.totalChapters || 0} Ch</span>
              <span className="novel-card__stat-sep" aria-hidden="true">•</span>
              <span className="novel-card__stat-compact">{formatViews(novel.viewCount)} views</span>
            </div>
          </div>
        </>
      ) : (
        <>
          <CoverImage
            coverImageUrl={getNovelImageUrl(novel)}
            title={novel.title}
            fallback="📚"
            loading={loading}
          >
            {rankDisplay && (
              <div className={joinClasses('novel-card__rank-badge', rankDisplay.cssModifier && `novel-card__rank-badge--${rankDisplay.cssModifier}`)}>
                <div className="rank-badge__main">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="rank-crown"><path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14"/></svg>
                  <span className="rank-number">#{rankDisplay.rankPosition}</span>
                </div>
                {rankDisplay.label && (
                  <span className="rank-badge__type">{rankDisplay.label}</span>
                )}
              </div>
            )}
            
            {status && (
              <div className="novel-card__status-badge">
                 <span className={`status-dot ${status.toLowerCase()}`}></span>
                 {status}
              </div>
            )}

            {coverBadge}

            {novel.contentRating === 'MATURE' && (
              <div
                className="novel-card__mature-badge"
                title={getMatureBadgeTitle(novel)}
              >18+</div>
            )}

            <div className="novel-card__info-overlay">
              <h3 className="novel-card__title">
                <HighlightedText text={novel.title} highlight={highlight} />
              </h3>
              {novel.author?.name && (
                <p className="novel-card__author-row">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon-pen"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>
                  <HighlightedText text={novel.author.name} highlight={highlight} />
                </p>
              )}
            </div>
          </CoverImage>

          <div className="novel-card__content">
            {renderGenres(genres || novel.genres)}
            
            <div className="novel-card__divider"></div>

            {customFooter ? (
              <div className="novel-card__stats-footer">
                {customFooter}
              </div>
            ) : actions && !metaItems ? (
               null
            ) : metaItems ? (
              <div className="novel-card__stats-footer novel-card__stats-footer--meta">
                {buildMetaItems(metaItems)}
              </div>
            ) : (
              <div className="novel-card__stats-footer">
                <div className="stat-col">
                  <span className="stat-value">
                    {novel.averageRating ? novel.averageRating.toFixed(1) : 'N/A'}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="icon-star-footer"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                  </span>
                  <span className="stat-label">
                    <span className="label-desktop">RATING</span>
                    <span className="label-mobile">RATE</span>
                  </span>
                </div>
                <div className="stat-separator"></div>
                <div className="stat-col">
                  <span className="stat-value">{novel.totalChapters || 0}</span>
                  <span className="stat-label">
                    <span className="label-desktop">CHAPTERS</span>
                    <span className="label-mobile">CH</span>
                  </span>
                </div>
                <div className="stat-separator"></div>
                <div className="stat-col">
                  <span className="stat-value">{formatViews(novel.viewCount)}</span>
                  <span className="stat-label">
                    <span className="label-desktop">VIEWS</span>
                    <span className="label-mobile">VIEW</span>
                  </span>
                </div>
              </div>
            )}

            {actions ? <div className="novel-card__actions">{actions}</div> : null}
          </div>
        </>
      )}
    </>
  );

  const cardProps = {
    className: joinClasses(
      'novel-card',
      `novel-card--${variant}`,
      rankDisplay && `novel-card--ranked`,
      className,
      isInteractive && 'novel-card--interactive'
    ),
    padded: false,
    hoverable: isInteractive,
    glass: true,
  };

  if (isLink) {
    return (
      <Link to={to} className="novel-card-wrapper">
        <Card {...cardProps} style={{ height: '100%', flexGrow: 1 }}>
          {cardContent}
        </Card>
      </Link>
    );
  }

  if (onClick) {
    return (
      <div onClick={onClick} className="novel-card-wrapper" style={{ cursor: 'pointer' }}>
        <Card {...cardProps} style={{ height: '100%', flexGrow: 1 }}>
          {cardContent}
        </Card>
      </div>
    );
  }

  return (
    <Card {...cardProps} style={{ height: '100%' }}>
      {cardContent}
    </Card>
  );
};

export default NovelCard;
